import React from 'react';
import { StringParam, UrlUpdateType, useQueryParams } from 'use-query-params';

import { ResultsExportType } from './exportTypes';

const resultsExportQueryParams = {
  resultsExportStudyId: StringParam,
  resultsExportType: StringParam,
  resultsExportStage: StringParam,
  featuresExportScope: StringParam,
  featuresExportExcludedCases: StringParam,
  metadataExportScope: StringParam,
  polygonsExportScope: StringParam,
  polygonsExportExcludedCases: StringParam,
  workflowExportSource: StringParam,
  workflowExportRun: StringParam,
};

type ResultsExportQuery = Partial<Record<keyof typeof resultsExportQueryParams, string | null | undefined>>;

const clearedQuery: ResultsExportQuery = {
  resultsExportStudyId: undefined,
  resultsExportType: undefined,
  resultsExportStage: undefined,
  featuresExportScope: undefined,
  featuresExportExcludedCases: undefined,
  metadataExportScope: undefined,
  polygonsExportScope: undefined,
  polygonsExportExcludedCases: undefined,
  workflowExportSource: undefined,
  workflowExportRun: undefined,
};

const draftQueryKeysByType: Partial<Record<ResultsExportType, (keyof ResultsExportQuery)[]>> = {
  features: ['featuresExportScope', 'featuresExportExcludedCases'],
  metadata: ['metadataExportScope'],
  polygons: ['polygonsExportScope', 'polygonsExportExcludedCases'],
};

// Dialog state lives in the URL so it survives a remount or reload; each draft key belongs to one export type.
export const useResultsExportUrlState = () => {
  const [query, setQuery] = useQueryParams(resultsExportQueryParams);

  const update = React.useCallback(
    (changes: ResultsExportQuery, updateType: UrlUpdateType = 'replaceIn') => setQuery(changes, updateType),
    [setQuery]
  );

  const clearDraft = React.useCallback(
    (type: ResultsExportType) => {
      const changes: ResultsExportQuery = {};
      draftQueryKeysByType[type]?.forEach((key) => {
        changes[key] = undefined;
      });
      update(changes);
    },
    [update]
  );

  return { query, update, clearDraft };
};

// One study's export dialog is open at a time; closing clears every export parameter.
export const useResultsExportDialogHost = (studyId?: string) => {
  const { query, update } = useResultsExportUrlState();
  const isOpen = Boolean(studyId) && query.resultsExportStudyId === studyId;

  const open = React.useCallback(
    () => update({ ...clearedQuery, resultsExportStudyId: studyId }, 'pushIn'),
    [update, studyId]
  );
  const close = React.useCallback(() => update(clearedQuery), [update]);

  return { isOpen, open, close };
};

const workflowSources = ['published', 'approved', 'run'] as const;
const RUN_SEPARATOR = ',';

// Absent means no run was chosen; an empty value means the user cleared the choice.
export const parseWorkflowExportRunIds = (value?: string | null): string[] | undefined =>
  value === undefined || value === null ? undefined : value.split(RUN_SEPARATOR).filter(Boolean);

// The Workflow export dialogs keep their results source in the URL, apart from the Workflow tab's own mode.
// The runs are a comma-separated list, so a single stored run id still restores as one run.
export const useWorkflowExportSourceUrlState = () => {
  const { query, update } = useResultsExportUrlState();
  const source = workflowSources.find((candidate) => candidate === query.workflowExportSource);
  const select = React.useCallback(
    (selection: { source: (typeof workflowSources)[number]; runIds?: string[] }) =>
      update({
        workflowExportSource: selection.source,
        workflowExportRun: selection.runIds ? selection.runIds.join(RUN_SEPARATOR) : undefined,
      }),
    [update]
  );
  const clear = React.useCallback(
    () => update({ workflowExportSource: undefined, workflowExportRun: undefined }),
    [update]
  );
  return {
    source,
    runIds: source === 'run' ? parseWorkflowExportRunIds(query.workflowExportRun) : undefined,
    select,
    clear,
  };
};
